import { Injectable } from '@angular/core';
import { Observable, of, delay, throwError, from } from 'rxjs';
import { LogError, LogStatus, Metadata } from '@memberjunction/core';
import { GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';
import { 
  FileUploadRequest, 
  FileRecord, 
  CreateFileResponse, 
  FileListResponse,
  UserDocumentData,
  DocumentCategory,
  DocumentCategoryData
} from '../utils/interfaces';
import { DataFetchService } from './data-fetch.service';
import { UserService } from './user.service';
import { Utils } from '../utils/utils';

/**
 * User Document Storage Service
 * Integrates with DocumentOperationsResolver for user-centric document operations
 * Uses MemberJunction's GraphQL API and Azure blob storage
 * Each user has their own private document repository
 */
@Injectable({
  providedIn: 'root'
})
export class FileStorageService {
  private categoryCache: Map<string, string> = new Map();
  
  constructor(
    private dataFetchService: DataFetchService,
    private userService: UserService,
    private utils: Utils
  ) {
    // Initialize category cache
    this.loadCategoryCache();
  }

  /**
   * Creates document upload request via DocumentOperationsResolver
   * Returns document ID and secure upload URL from Azure Storage
   */
  createFile(fileRequest: FileUploadRequest): Observable<CreateFileResponse> {
    const mutation = `
      mutation CreateDocumentUpload($input: DocumentUploadInput!) {
        createDocumentUpload(input: $input) {
          success
          documentId
          uploadUrl
          message
          errorDetails
        }
      }
    `;

    const variables = {
      input: {
        name: fileRequest.Name,
        description: fileRequest.Description || '',
        categoryId: this.getCategoryId(fileRequest.Category || 'general-documents'),
        tags: fileRequest.Tags || null,
        isFavorite: fileRequest.IsFavorite || false,
        contentType: fileRequest.ContentType || 'application/octet-stream',
        size: fileRequest.Size || 0
      }
    };

    return from(
      GraphQLDataProvider.ExecuteGQL(mutation, variables).then(response => {
        const result = response.createDocumentUpload;
        
        if (!result.success) {
          throw new Error(result.message || result.errorDetails || 'Failed to create document upload');
        }

        const fileRecord: FileRecord = {
          ID: result.documentId,
          Name: this.sanitizeFileName(fileRequest.Name),
          OriginalName: fileRequest.Name,
          ProviderID: 'azure-blob-provider',
          Status: 'Pending',
          UserID: this.userService.getUserInfo()?.ID || 'current-user',
          Category: fileRequest.Category || 'general-documents',
          ContentType: fileRequest.ContentType || 'application/octet-stream',
          Size: fileRequest.Size || 0,
          Tags: fileRequest.Tags,
          IsFavorite: fileRequest.IsFavorite || false,
          UploadedBy: 'Current User',
          UploadDate: new Date().toISOString()
        };

        return {
          success: true,
          file: fileRecord,
          uploadUrl: result.uploadUrl
        };
      }).catch(error => {
        LogError('Document upload creation failed:', undefined, error);
        throw error;
      })
    );
  }

  /**
   * Uploads file directly to Azure Storage using pre-authenticated URL
   * Uses fetch API to upload directly to Azure Blob Storage
   */
  uploadFileToStorage(uploadUrl: string, file: File): Observable<{ success: boolean; error?: string }> {
    LogStatus(`Uploading ${file.name} to Azure Storage`);
    
    return from(
      fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type
        }
      }).then(response => {
        if (!response.ok) {
          throw new Error(`Azure upload failed: ${response.status} ${response.statusText}`);
        }
        
        LogStatus(`Successfully uploaded ${file.name} to Azure Storage`);
        return { success: true };
      }).catch(error => {
        LogError('File upload to Azure failed:', undefined, error);
        return { 
          success: false, 
          error: error.message || 'Upload to storage failed' 
        };
      })
    );
  }

  /**
   * File status is managed automatically by DocumentOperationsResolver
   * This method is retained for compatibility but may not be needed
   */
  updateFileStatus(fileId: string, status: 'Uploaded' | 'Failed', downloadUrl?: string): Observable<{ success: boolean }> {
    // With DocumentOperationsResolver, file status is managed automatically
    // The document record is created with proper status tracking
    LogStatus(`Document ${fileId} status would be updated to: ${status}`);
    return of({ success: true });
  }

  /**
   * Get documents for current user using direct RunView (user-centric repository)
   */
  getUserFiles(category?: string, searchTerm?: string): Observable<FileListResponse> {
    // Build filter conditions - user can only see their own documents
    const currentUser = this.userService.getUserInfo();
    let extraFilter = `IsActive = 1 AND Status = 'Active' AND UserID = '${currentUser?.ID || 'no-user'}'`;
    
    if (category && category !== 'All') {
      // Convert category name to ID using cache
      const categoryId = this.getCategoryId(category);
      extraFilter += ` AND CategoryID = '${categoryId}'`;
    }
    
    if (searchTerm && searchTerm.trim()) {
      extraFilter += ` AND (Name LIKE '%${searchTerm}%' OR Description LIKE '%${searchTerm}%')`;
    }

    return from(
      this.dataFetchService.fetchEntityData<UserDocumentData>(
        'User Documents',
        extraFilter,
        'UploadDate DESC',
        'simple'
      ).then(async (documents: UserDocumentData[]) => {
        // Pre-load download URLs for better UX
        const fileRecordsPromises = documents.map(async (doc: UserDocumentData) => {
          let downloadUrl = '';
          
          try {
            // Pre-fetch download URL for preview/download
            const urlResponse = await this.getDownloadUrl(doc.ID).toPromise();
            downloadUrl = urlResponse?.downloadUrl || '';
          } catch (error) {
            LogError(`Failed to pre-load download URL for document ${doc.ID}:`, undefined, error);
            // Continue without download URL - will fetch on demand
          }
          
          return {
            ID: doc.ID,
            Name: doc.Name,
            OriginalName: doc.OriginalName || doc.Name,
            Description: doc.Description || undefined,
            ProviderID: 'azure-blob-provider',
            Status: doc.Status,
            UserID: doc.UserID,
            Category: doc.Category || 'general-documents',
            ContentType: doc.ContentType,
            Size: doc.Size,
            Tags: doc.Tags || undefined,
            IsFavorite: doc.IsFavorite || false,
            IsArchived: doc.IsArchived || false,
            UploadedBy: 'You',
            UploadDate: new Date(doc.UploadDate).toISOString(),
            LastAccessedDate: doc.LastAccessedDate ? new Date(doc.LastAccessedDate).toISOString() : undefined,
            DownloadUrl: downloadUrl
          } as FileRecord;
        });

        const fileRecords = await Promise.all(fileRecordsPromises);

        return {
          files: fileRecords,
          total: fileRecords.length
        };
      }).catch(error => {
        LogError('Failed to get user documents:', undefined, error);
        return {
          files: [],
          total: 0
        };
      })
    );
  }

  /**
   * Get favorite documents for current user
   */
  getFavoriteFiles(): Observable<FileListResponse> {
    // Build filter conditions - only user's favorite documents
    const currentUser = this.userService.getUserInfo();
    let extraFilter = `IsActive = 1 AND Status = 'Active' AND UserID = '${currentUser?.ID || 'no-user'}' AND IsFavorite = 1`;

    return from(
      this.dataFetchService.fetchEntityData<UserDocumentData>(
        'User Documents',
        extraFilter,
        'LastAccessedDate DESC, UploadDate DESC',
        'simple'
      ).then((documents: UserDocumentData[]) => {
        const fileRecords: FileRecord[] = documents.map((doc: UserDocumentData) => ({
          ID: doc.ID,
          Name: doc.Name,
          OriginalName: doc.OriginalName || doc.Name,
          Description: doc.Description || undefined,
          ProviderID: 'azure-blob-provider',
          Status: doc.Status,
          UserID: doc.UserID,
          Category: doc.Category || 'general-documents',
          ContentType: doc.ContentType,
          Size: doc.Size,
          Tags: doc.Tags || undefined,
          IsFavorite: true, // All results are favorites
          IsArchived: doc.IsArchived || false,
          UploadedBy: 'You',
          UploadDate: new Date(doc.UploadDate).toISOString(),
          LastAccessedDate: doc.LastAccessedDate ? new Date(doc.LastAccessedDate).toISOString() : undefined,
          DownloadUrl: ''
        }));

        return {
          files: fileRecords,
          total: fileRecords.length
        };
      }).catch(error => {
        LogError('Failed to get favorite documents:', undefined, error);
        return {
          files: [],
          total: 0
        };
      })
    );
  }

  /**
   * Delete a document via DocumentOperationsResolver
   */
  deleteFile(fileId: string): Observable<{ success: boolean }> {
    const mutation = `
      mutation DeleteDocument($documentId: String!) {
        deleteDocument(documentId: $documentId)
      }
    `;

    return from(
      GraphQLDataProvider.ExecuteGQL(mutation, { documentId: fileId }).then(response => {
        const success = response.deleteDocument;
        
        if (success) {
          LogStatus(`Successfully deleted document ${fileId}`);
        } else {
          LogError(`Failed to delete document ${fileId}`);
        }

        return { success };
      }).catch(error => {
        LogError('Failed to delete document:', undefined, error);
        return { success: false };
      })
    );
  }

  /**
   * Get secure download URL for a document via DocumentOperationsResolver
   */
  getDownloadUrl(fileId: string): Observable<{ downloadUrl: string } | null> {
    const query = `
      query GetDocumentDownloadUrl($documentId: String!) {
        getDocumentDownloadUrl(documentId: $documentId) {
          success
          downloadUrl
          expiresAt
          message
        }
      }
    `;

    return from(
      GraphQLDataProvider.ExecuteGQL(query, { documentId: fileId }).then(response => {
        const result = response.getDocumentDownloadUrl;
        
        if (!result.success) {
          LogError(`Failed to get download URL for document ${fileId}:`, undefined, result.message);
          return null;
        }

        return { downloadUrl: result.downloadUrl };
      }).catch(error => {
        LogError('Failed to get download URL:', undefined, error);
        return null;
      })
    );
  }

  /**
   * Utility: Sanitize file names for storage
   */
  private sanitizeFileName(fileName: string): string {
    return fileName
      .toLowerCase()
      .replace(/[^a-z0-9.-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  /**
   * Load category cache on initialization
   */
  private async loadCategoryCache(): Promise<void> {
    try {
      const categories = await this.dataFetchService.fetchEntityData<any>(
        'Document Categories',
        'IsActive = 1',
        'Name ASC',  // Order by Name field instead
        'simple'
      );
      
      // Cache category Name field to ID mapping (Name is the stable identifier)
      categories.forEach(cat => {
        if (cat.Name) {
          this.categoryCache.set(cat.Name.toLowerCase(), cat.ID);
        }
        // Also cache by DisplayName for backward compatibility
        const displayName = cat.DisplayName || cat.Name;
        if (displayName) {
          this.categoryCache.set(displayName.toLowerCase(), cat.ID);
        }
      });
      
      LogStatus('Category cache loaded with ' + this.categoryCache.size + ' categories');
    } catch (error) {
      LogError('Failed to load category cache:', undefined, error);
      // Set some default category IDs if available
      // These would need to match actual IDs in your database
    }
  }

  /**
   * Get category display name from category ID
   */
  private getCategoryDisplayName(categoryId: string): string {
    // Find category by ID from cache
    for (const [key, value] of this.categoryCache.entries()) {
      if (value === categoryId) {
        // Convert key back to display name - key could be either name or displayName
        // Try to find a better display name if this looks like a name (has dashes)
        if (key.includes('-')) {
          // This is likely a name, convert to display name
          return key.split('-').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
        } else {
          // This is likely already a display name
          return key.charAt(0).toUpperCase() + key.slice(1);
        }
      }
    }
    
    // Fallback - return the ID or convert if it looks like a name
    if (categoryId && categoryId.includes('-')) {
      return categoryId.split('-').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }
    
    return categoryId || 'Unknown Category';
  }

  /**
   * Get category ID from category name
   */
  private getCategoryId(categoryName: string): string {
    // Check cache first
    const cachedId = this.categoryCache.get(categoryName.toLowerCase());
    if (cachedId) {
      return cachedId;
    }
    
    // If not in cache, try to reload cache asynchronously for next time
    // But don't wait for it - return fallback immediately
    this.loadCategoryCache().then(() => {
      const id = this.categoryCache.get(categoryName.toLowerCase());
      if (!id) {
        LogError('Category still not found after cache reload: ' + categoryName);
      }
    });
    
    // Return a placeholder ID - the actual ID will be determined by the database
    LogError('Category not found in cache, using general-documents fallback: ' + categoryName);
    return 'general-documents-fallback'; // Will be resolved on backend
  }

  /**
   * Get available document categories with full details (more efficient)
   */
  getFileCategories(): Observable<Array<{name: string, displayName: string, description: string}>> {
    return from(
      this.dataFetchService.fetchEntityData<DocumentCategoryData>(
        'Document Categories',
        'IsActive = 1',
        'DisplayName ASC',
        'simple'
      ).then((categories: DocumentCategoryData[]) => {
        // Update cache while we're at it
        categories.forEach((cat: DocumentCategoryData) => {
          if (cat.Name) {
            this.categoryCache.set(cat.Name.toLowerCase(), cat.ID);
          }
          // Also cache by DisplayName for backward compatibility
          const displayName = cat.DisplayName || cat.Name;
          if (displayName) {
            this.categoryCache.set(displayName.toLowerCase(), cat.ID);
          }
        });
        
        // Return structured category data with Name field as identifier
        return categories.map((cat: DocumentCategoryData) => ({
          name: cat.Name,  // Use Name as the stable identifier
          displayName: cat.DisplayName || cat.Name,
          description: cat.Description || `${cat.DisplayName || cat.Name} documents and materials`
        }));
      }).catch(error => {
        LogError('Failed to get document categories:', undefined, error);
        // Fallback to actual database categories using Name field
        return [
          { name: 'meeting-minutes', displayName: 'Meeting Minutes', description: 'Official records of chapter meetings' },
          { name: 'meeting-agendas', displayName: 'Meeting Agendas', description: 'Upcoming meeting agendas and materials' },
          { name: 'financial-reports', displayName: 'Financial Reports', description: 'Budget reports, financial statements, and treasurer documents' },
          { name: 'invoices-receipts', displayName: 'Invoices & Receipts', description: 'Payment records and financial transactions' },
          { name: 'bylaws-policies', displayName: 'Bylaws & Policies', description: 'Chapter governing documents and policies' },
          { name: 'forms-applications', displayName: 'Forms & Applications', description: 'Membership forms, applications, and official documents' },
          { name: 'event-materials', displayName: 'Event Materials', description: 'Event planning documents, flyers, and promotional materials' },
          { name: 'educational-content', displayName: 'Educational Content', description: 'Training materials, presentations, and educational resources' },
          { name: 'announcements', displayName: 'Announcements', description: 'Chapter announcements and communications' },
          { name: 'general-documents', displayName: 'General Documents', description: 'Miscellaneous chapter documents' }
        ];
      })
    );
  }

  /**
   * Complete file upload workflow for user documents
   * Combines all steps: create record, upload to storage, update status
   */
  async uploadFile(
    file: File, 
    category?: string, 
    description?: string,
    tags?: string,
    isFavorite?: boolean
  ): Promise<FileRecord> {
    try {
      // Step 1: Create file record and get upload URL
      const createResponse = await this.createFile({
        Name: file.name,
        ProviderID: 'azure-blob-provider',
        Status: 'Pending',
        Category: category,
        Description: description,
        Tags: tags,
        IsFavorite: isFavorite,
        ContentType: file.type,
        Size: file.size
      }).toPromise();

      if (!createResponse?.success || !createResponse.file || !createResponse.uploadUrl) {
        throw new Error('Failed to create file record');
      }

      // Step 2: Upload file to storage
      const uploadResponse = await this.uploadFileToStorage(createResponse.uploadUrl, file).toPromise();
      
      if (!uploadResponse?.success) {
        throw new Error('Failed to upload file');
      }

      // Step 3: File status is automatically updated by DocumentOperationsResolver
      // Log the successful upload
      await this.logDocumentAccess(createResponse.file.ID, 'Upload');

      // Return final file record
      return {
        ...createResponse.file,
        Status: 'Active'
      };

    } catch (error) {
      LogError('File upload failed:', undefined, error);
      throw error;
    }
  }


  /**
   * Log document access using MemberJunction Entity Object approach
   * More efficient than custom GraphQL mutations - uses generated entities
   */
  async logDocumentAccess(
    documentId: string, 
    accessType: 'View' | 'Download' | 'Upload' | 'Delete', 
    userId?: string
  ): Promise<void> {
    try {
      LogStatus(`Logging document access: ${documentId} - ${accessType} by user ${userId || 'current'}`);
      
      // Get the current user if not provided
      const currentUser = this.userService.getUserInfo();
      const effectiveUserId = userId || currentUser?.ID || 'system';
      
      // Use MemberJunction's Metadata system to get the entity
      const md = new Metadata();
      const accessLogEntity = await md.GetEntityObject('Document Access Logs', currentUser);
      
      if (accessLogEntity) {
        // Create new record
        accessLogEntity.NewRecord();
        
        // Set the properties
        accessLogEntity.Set('DocumentID', documentId);
        accessLogEntity.Set('UserID', effectiveUserId);
        accessLogEntity.Set('AccessType', accessType);
        accessLogEntity.Set('AccessDate', new Date());
        accessLogEntity.Set('IPAddress', await this.utils.getClientIP());
        accessLogEntity.Set('UserAgent', navigator.userAgent);
        accessLogEntity.Set('URL', window.location.href);
        
        // Save the record
        const result = await accessLogEntity.Save();
        
        if (result) {
          LogStatus(`Successfully logged ${accessType} access for document ${documentId}, ID: ${accessLogEntity.Get('ID')}`);
        } else {
          throw new Error(`Save failed: ${accessLogEntity.LatestResult.Message}`);
        }
      } else {
        throw new Error('Could not get Document Access Logs entity');
      }
      
    } catch (error) {
      LogError('Failed to log document access:', undefined, error);
      // Don't throw - access logging shouldn't fail main operations
      
      // Fallback: Log to console with structured data for potential collection
      LogStatus('DocumentAccess:', undefined, {
        timestamp: new Date().toISOString(),
        documentId,
        accessType,
        userId: userId || 'current',
        userAgent: navigator.userAgent,
        url: window.location.href,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  /**
   * Toggle favorite status for a user document
   */
  toggleFavorite(fileId: string, isFavorite: boolean): Observable<{ success: boolean }> {
    const mutation = `
      mutation UpdateDocument($documentId: String!, $input: DocumentUpdateInput!) {
        updateDocument(documentId: $documentId, input: $input) {
          success
          message
        }
      }
    `;

    return from(
      GraphQLDataProvider.ExecuteGQL(mutation, { 
        documentId: fileId, 
        input: { IsFavorite: isFavorite } 
      }).then(response => {
        const result = response.updateDocument;
        
        if (result.success) {
          LogStatus(`Successfully ${isFavorite ? 'added to' : 'removed from'} favorites: ${fileId}`);
        } else {
          LogError(`Failed to update favorite status for document ${fileId}:`, undefined, result.message);
        }

        return { success: result.success };
      }).catch(error => {
        LogError('Failed to update favorite status:', undefined, error);
        return { success: false };
      })
    );
  }
}